import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, Send } from 'lucide-react';
import type { MailDelivery, MailEncryption, MailSettings, MailTestResult } from '@simpleacs/shared';
import { api } from '../../lib/api';
import { formatRelative } from '../../lib/format';
import {
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Pill,
  TBody,
  THead,
  TRow,
  Table,
} from '../../components/ui';

/**
 * Where the platform's mail goes out from, and what has actually gone.
 *
 * Two halves on purpose. The top half is configuration and reads like every
 * other credential card here: the password is write-only, an untouched field
 * never erases it, and the test button is next to the fields it proves rather
 * than somewhere else. The bottom half is the RECORD — the answer to "did that
 * invoice go out" — and it is on this page because this is where somebody
 * lands when they suspect it did not.
 *
 * Mail is optional. With nothing filled in the platform behaves exactly as it
 * does today: invoices are raised, payments settle, and every message is
 * written down as skipped with the reason. The banner at the top says so
 * rather than leaving a complete-looking form to imply otherwise.
 */
interface MailSettingsView extends MailSettings {
  pending: number;
  passwordUnreadable: boolean;
}

const ENCRYPTIONS: Array<{ id: MailEncryption; label: string; hint: string }> = [
  { id: 'tls', label: 'TLS', hint: 'Encrypted from the first byte — usually port 465.' },
  { id: 'starttls', label: 'STARTTLS', hint: 'Upgrade after connecting — usually port 587.' },
  { id: 'none', label: 'None', hint: 'No encryption. Only for a relay on this machine.' },
];

const STATUS_TONE: Record<MailDelivery['status'], 'success' | 'warn' | 'danger' | 'neutral'> = {
  sent: 'success',
  queued: 'warn',
  sending: 'warn',
  failed: 'danger',
  skipped: 'neutral',
};

const KIND_LABEL: Record<MailDelivery['kind'], string> = {
  test: 'Test',
  invoice_issued: 'Invoice issued',
  payment_receipt: 'Payment receipt',
  invoice_link: 'Invoice link sent',
  receipt_link: 'Receipt link sent',
  contact_code: 'Address confirmation',
  contact_notice: 'Contact-change warning',
  mfa_code: 'Sign-in code',
  welcome: 'Welcome (sign-in details)',
};

export function EmailTab() {
  const queryClient = useQueryClient();
  const settingsQuery = useQuery({
    queryKey: ['email', 'settings'],
    queryFn: () => api.get<MailSettingsView>('/email/settings'),
  });
  const deliveriesQuery = useQuery({
    queryKey: ['email', 'deliveries'],
    queryFn: () => api.get<{ items: MailDelivery[] }>('/email/deliveries', { limit: 25 }),
    // Something queued is something in flight; a slow relay should be visible
    // as it clears rather than after a manual refresh.
    refetchInterval: 15_000,
  });

  const [form, setForm] = useState({
    host: '',
    port: '587',
    encryption: 'starttls' as MailEncryption,
    username: '',
    password: '',
    fromName: '',
    fromAddress: '',
    replyTo: '',
  });
  const [testTo, setTestTo] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tested, setTested] = useState<MailTestResult | null>(null);

  const seeded = useRef(false);
  useEffect(() => {
    const s = settingsQuery.data;
    if (!s || seeded.current) return;
    seeded.current = true;
    setForm({
      host: s.host,
      port: String(s.port),
      encryption: s.encryption,
      username: s.username,
      // Never populated from the server: it is not sent to the browser, and a
      // box of dots the operator could "save" unchanged would be a lie about
      // what is stored.
      password: '',
      fromName: s.fromName,
      fromAddress: s.fromAddress,
      replyTo: s.replyTo,
    });
  }, [settingsQuery.data]);

  const save = useMutation({
    mutationFn: () =>
      api.put('/email/settings', {
        host: form.host,
        port: Number(form.port) || 587,
        encryption: form.encryption,
        username: form.username,
        // Empty means "leave whatever is stored alone".
        password: form.password || undefined,
        fromName: form.fromName,
        fromAddress: form.fromAddress,
        replyTo: form.replyTo,
      }),
    onSuccess: async () => {
      setError(null);
      setNotice('Saved.');
      setForm((f) => ({ ...f, password: '' }));
      setTimeout(() => setNotice(null), 4000);
      await queryClient.invalidateQueries({ queryKey: ['email'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not save'),
  });

  const clearPassword = useMutation({
    mutationFn: () => api.put('/email/settings', { clearPassword: true }),
    onSuccess: async () => {
      setNotice('Password removed.');
      await queryClient.invalidateQueries({ queryKey: ['email'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not remove the password'),
  });

  const test = useMutation({
    mutationFn: () =>
      api.post<MailTestResult>('/email/test', testTo ? { to: testTo } : {}),
    onSuccess: async (r) => {
      setTested(r);
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['email'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'The test could not be run'),
  });

  const s = settingsQuery.data;
  const deliveries = deliveriesQuery.data?.items ?? [];

  return (
    <div className="space-y-5">
      <Card
        title={
          <span className="flex items-center gap-2">
            <Mail className="size-4 text-muted" />
            Outgoing mail
          </span>
        }
        action={
          <Button
            size="sm"
            variant="ghost"
            icon={<Send className="size-3.5" />}
            loading={test.isPending}
            disabled={!s?.configured}
            onClick={() => test.mutate()}
          >
            Send a test
          </Button>
        }
      >
        <div className="space-y-5 p-5">
          <p className="text-sm text-muted">
            The relay this platform sends invoices and payment receipts through. It is optional:
            with nothing set here the platform works exactly as it does now, and every message it
            would have sent is written down below as skipped, with the reason. Nothing is ever sent
            to a subscriber — this is mail to the organizations this platform bills.
          </p>

          {s && !s.configured && (
            <p className="rounded-lg border border-warn/30 bg-warn-soft px-4 py-2.5 text-sm text-warn">
              Mail is switched off. A host and a from-address are what switch it on.
            </p>
          )}
          {s?.passwordUnreadable && (
            <p className="rounded-lg border border-danger/30 bg-danger-soft px-4 py-2.5 text-sm text-danger">
              The saved password cannot be decrypted — APP_SECRET_KEY has changed since it was
              saved. Enter the password again.
            </p>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="SMTP host" hint="The relay's hostname, e.g. smtp.your-provider.com">
              <Input
                value={form.host}
                placeholder="smtp.example.com"
                onChange={(e) => setForm({ ...form, host: e.target.value })}
              />
            </Field>
            <Field label="Port" hint="465 for TLS, 587 for STARTTLS, 25 for a local relay.">
              <Input
                value={form.port}
                inputMode="numeric"
                onChange={(e) => setForm({ ...form, port: e.target.value.replace(/\D/g, '') })}
              />
            </Field>
          </div>

          <div>
            <span className="label">Encryption</span>
            <div className="flex flex-wrap gap-2">
              {ENCRYPTIONS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setForm({ ...form, encryption: option.id })}
                  className={
                    form.encryption === option.id
                      ? 'rounded-lg border border-primary bg-primary-soft px-3 py-1.5 text-sm text-primary'
                      : 'rounded-lg border border-line px-3 py-1.5 text-sm text-muted hover:text-ink'
                  }
                >
                  {option.label}
                </button>
              ))}
            </div>
            <p className="mt-1 text-xs text-faint">
              {ENCRYPTIONS.find((e) => e.id === form.encryption)?.hint}
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Username" hint="Leave empty if the relay does not need a login.">
              <Input
                value={form.username}
                autoComplete="off"
                onChange={(e) => setForm({ ...form, username: e.target.value })}
              />
            </Field>
            <Field
              label="Password"
              hint={
                s?.passwordConfigured
                  ? 'A password is saved. Type a new one to replace it; leave it empty to keep it.'
                  : 'Stored encrypted, and never shown again.'
              }
            >
              <Input
                type="password"
                value={form.password}
                autoComplete="new-password"
                placeholder={s?.passwordConfigured ? '••••••••' : ''}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </Field>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="From name" hint="What a recipient sees as the sender.">
              <Input
                value={form.fromName}
                placeholder="SimpleACS Billing"
                onChange={(e) => setForm({ ...form, fromName: e.target.value })}
              />
            </Field>
            <Field label="From address" hint="Must be an address the relay will send as.">
              <Input
                value={form.fromAddress}
                placeholder="billing@your-domain.com"
                onChange={(e) => setForm({ ...form, fromAddress: e.target.value })}
              />
            </Field>
          </div>

          <Field label="Reply-to" hint="Optional. Where a reply should go, if not the from address.">
            <Input
              value={form.replyTo}
              onChange={(e) => setForm({ ...form, replyTo: e.target.value })}
            />
          </Field>

          <div className="flex flex-wrap items-center gap-3">
            <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
              Save
            </Button>
            {s?.passwordConfigured && (
              <Button
                variant="ghost"
                loading={clearPassword.isPending}
                onClick={() => clearPassword.mutate()}
              >
                Remove the password
              </Button>
            )}
            {notice && <span className="text-sm text-success">{notice}</span>}
            {error && <span className="text-sm text-danger">{error}</span>}
          </div>

          {/* --- Proving it works ------------------------------------------ */}
          <div className="space-y-2 rounded-lg border border-line p-4">
            <p className="text-sm font-semibold text-ink">Prove it</p>
            <p className="text-xs text-muted">
              Sends one message now and waits for the server, so a wrong password says so here
              instead of at two in the morning on the invoice run. Defaults to your own address.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                className="max-w-xs"
                value={testTo}
                placeholder="you@your-domain.com"
                onChange={(e) => setTestTo(e.target.value)}
              />
              <Button
                size="sm"
                icon={<Send className="size-3.5" />}
                loading={test.isPending}
                disabled={!s?.configured}
                onClick={() => test.mutate()}
              >
                Send a test
              </Button>
            </div>
            {tested && (
              <p className={tested.ok ? 'text-sm text-success' : 'text-sm text-danger'}>
                {tested.message}
                {tested.ok ? ` (${tested.ms} ms)` : ''}
              </p>
            )}
          </div>
        </div>
      </Card>

      <Card
        title="What has been sent"
        action={
          s && s.pending > 0 ? (
            <Pill tone="warn">{s.pending} waiting</Pill>
          ) : (
            <Pill tone="neutral">nothing waiting</Pill>
          )
        }
      >
        {deliveries.length === 0 ? (
          <EmptyState
            title="No mail yet"
            description="Every invoice issued and every payment confirmed is recorded here — including the ones that were never sent, and why."
          />
        ) : (
          <Table>
            <THead>
              <th className="th">When</th>
              <th className="th">What</th>
              <th className="th">To</th>
              <th className="th">Subject</th>
              <th className="th">Outcome</th>
            </THead>
            <TBody>
              {deliveries.map((d) => (
                <TRow key={d.id}>
                  <td className="td whitespace-nowrap text-muted">{formatRelative(d.createdAt)}</td>
                  <td className="td whitespace-nowrap">
                    {KIND_LABEL[d.kind]}
                    {d.orgName ? <span className="text-faint"> · {d.orgName}</span> : null}
                  </td>
                  <td className="td whitespace-nowrap">{d.toAddress}</td>
                  <td className="td max-w-xs truncate">{d.subject}</td>
                  <td className="td">
                    <div className="flex flex-col gap-1">
                      <span>
                        <Pill state tone={STATUS_TONE[d.status]}>{d.status}</Pill>
                        {d.attempts > 1 && (
                          <span className="ml-2 text-xs text-faint">{d.attempts} attempts</span>
                        )}
                      </span>
                      {d.error && <span className="text-xs text-muted">{d.error}</span>}
                    </div>
                  </td>
                </TRow>
              ))}
            </TBody>
          </Table>
        )}
      </Card>
    </div>
  );
}
